/**
 * Step2_ProdottoSelect — Selezione prodotto con checkbox (stile MSSP).
 * Modalità "Componi" (base + add-on) OPPURE "Bundle" pre-configurati.
 * Invia i prodotti selezionati al wizard tramite onProductMeta.
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { ShoppingBag, Shield, Wifi, Eye, Monitor, Package, ChevronDown } from 'lucide-react';
import PricingCalculator from '../PricingCalculator';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function StepProdottoSelect({ data, onChange, onProductMeta, errors }) {
  const [allProdotti, setAllProdotti] = useState([]);
  const [perTipo, setPerTipo] = useState({});
  const [loadingProdotti, setLoadingProdotti] = useState(true);

  // Scroll arrow
  const [showArrow, setShowArrow] = useState(false);
  const containerRef = useRef(null);

  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setShowArrow(
      el.scrollHeight > el.clientHeight + 10 &&
      el.scrollTop + el.clientHeight < el.scrollHeight - 10
    );
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    checkOverflow();
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow, loadingProdotti]);

  const scrollDown = () => {
    const el = containerRef.current;
    if (el) el.scrollBy({ top: 200, behavior: 'smooth' });
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/store/listino/`)
      .then((res) => {
        setAllProdotti(res.data.prodotti || []);
        setPerTipo(res.data.per_tipo || {});
      })
      .catch(() => { setAllProdotti([]); setPerTipo({}); })
      .finally(() => setLoadingProdotti(false));
  }, []);

  const prodottoBase = useMemo(() => (perTipo.base || [])[0] || null, [perTipo]);
  const prodottoNS = useMemo(() => (perTipo.network_surveillance || [])[0] || null, [perTipo]);
  const prodottoAwareness = useMemo(() => (perTipo.awareness || [])[0] || null, [perTipo]);
  const prodottoMSSP = useMemo(() => (perTipo.mssp_monitoring || [])[0] || null, [perTipo]);
  const bundles = useMemo(() => perTipo.bundle || [], [perTipo]);

  // Mode: 'componi' or 'bundle'
  const mode = data._bundleId ? 'bundle' : 'componi';

  // Notify parent of product objects whenever they change
  useEffect(() => {
    if (!onProductMeta) return;
    const selectedId = data._bundleId || data.listino_prodotto_base;
    const selected = allProdotti.find((p) => p.id === selectedId) || null;
    onProductMeta({
      selectedProdotto: selected,
      prodottoNS,
      prodottoAwareness,
      prodottoMSSP,
    });
  }, [data.listino_prodotto_base, data._bundleId, allProdotti, prodottoNS, prodottoAwareness, prodottoMSSP, onProductMeta]);

  // ── Handlers: Componi ──────────────────────────────────────
  const isBaseSelected = !!data.listino_prodotto_base && !data._bundleId && prodottoBase &&
    (parseInt(data.listino_prodotto_base) === prodottoBase.id);

  const handleBaseToggle = (checked) => {
    if (checked && prodottoBase) {
      onChange({
        ...data,
        _bundleId: null,
        listino_prodotto_base: prodottoBase.id,
        numero_endpoint: Math.max(data.numero_endpoint || 0, prodottoBase.numero_endpoint || 10),
      });
    } else {
      onChange({
        ...data,
        _bundleId: null,
        listino_prodotto_base: null,
        numero_endpoint: 10,
        offerta_sorveglianza_rete: '',
        awareness_enabled: false,
        awareness_seats: 0,
        servizio_mssp_monitoring: false,
        gettoni_totali: 0,
      });
    }
  };

  const handleNSToggle = (checked) => {
    onChange({ ...data, offerta_sorveglianza_rete: checked ? 'FLAT' : '' });
  };

  const handleAwarenessToggle = (checked) => {
    onChange({
      ...data,
      awareness_enabled: checked,
      awareness_seats: checked ? (data.awareness_seats || prodottoAwareness?.numero_awareness_seats || 10) : 0,
    });
  };

  const handleMSSPToggle = (checked) => {
    onChange({
      ...data,
      servizio_mssp_monitoring: checked,
      gettoni_totali: checked ? (data.gettoni_totali || prodottoMSSP?.numero_gettoni || 20) : 0,
    });
  };

  // ── Handler: Bundle ────────────────────────────────────────
  const handleBundleSelect = (bundle) => {
    if (data._bundleId === bundle.id) {
      // Deselect
      onChange({
        ...data,
        _bundleId: null,
        listino_prodotto_base: null,
        numero_endpoint: 10,
        offerta_sorveglianza_rete: '',
        awareness_enabled: false,
        awareness_seats: 0,
        servizio_mssp_monitoring: false,
        gettoni_totali: 0,
      });
    } else {
      // Select bundle
      onChange({
        ...data,
        _bundleId: bundle.id,
        listino_prodotto_base: bundle.id,
        numero_endpoint: Math.max(data.numero_endpoint || 0, bundle.numero_endpoint || 10),
        awareness_enabled: bundle.awareness_inclusa || false,
        awareness_seats: bundle.numero_awareness_seats || 0,
        offerta_sorveglianza_rete: (bundle.descrizione_breve || '').toLowerCase().includes('network') ? 'FLAT' : '',
        servizio_mssp_monitoring: !!(bundle.numero_gettoni),
        gettoni_totali: bundle.numero_gettoni || 0,
      });
    }
  };

  const handleEndpointChange = (e) => {
    const raw = parseInt(e.target.value, 10) || 0;
    const val = Math.ceil(raw / 10) * 10;
    onChange({ ...data, numero_endpoint: val });
  };

  const fieldError = (field) => {
    const err = errors?.[`ordine.${field}`] || errors?.[field];
    if (!err) return null;
    const msg = Array.isArray(err) ? err[0] : err;
    return <p className="text-red-500 text-xs mt-1">{msg}</p>;
  };

  // Selected product for pricing
  const selectedProdotto = allProdotti.find(
    (p) => p.id === (data._bundleId || data.listino_prodotto_base)
  ) || null;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <ShoppingBag className="w-6 h-6 text-yellow-600" />
        <h2 className="text-xl font-bold text-gray-900">Scegli il Prodotto</h2>
      </div>

      {loadingProdotti ? (
        <div className="text-center py-12 text-gray-400">Caricamento prodotti...</div>
      ) : !prodottoBase ? (
        <div className="text-center py-12 text-gray-400">Nessun prodotto disponibile.</div>
      ) : (
        <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto pr-2">
          <div className={`grid gap-5 ${bundles.length > 0 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>

          {/* ━━ LEFT COLUMN ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          <div>
          {mode === 'bundle' && selectedProdotto ? (
            /* Bundle selezionato → a sinistra mostra il calcolatore prezzo */
            <div className="lg:sticky lg:top-0">
              <PricingCalculator
                prodotto={selectedProdotto}
                numEndpoint={data.numero_endpoint || 0}
                awarenessSeats={data.awareness_seats || 0}
                awarenessEnabled={data.awareness_enabled}
                prodottoAwareness={prodottoAwareness}
                nsEnabled={!!data.offerta_sorveglianza_rete}
                prodottoNS={prodottoNS}
                msspEnabled={data.servizio_mssp_monitoring || false}
                prodottoMSSP={prodottoMSSP}
                gettoniTotali={data.gettoni_totali || 0}
                isBundle={true}
              />
            </div>
          ) : (
            /* Default / Componi selezionato → mostra le opzioni componi */
            <>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Componi la tua offerta</h3>

            <div className="space-y-3">
              {/* Base */}
              <div className={`p-4 rounded-lg border ${isBaseSelected ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-white'} ${mode === 'bundle' ? 'opacity-40' : ''}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isBaseSelected}
                    disabled={mode === 'bundle'}
                    onChange={(e) => handleBaseToggle(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Shield size={16} className="text-yellow-600" />
                      <span className="text-sm font-medium text-gray-800">{prodottoBase.nome_prodotto}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      €{prodottoBase.prezzo_base}/{prodottoBase.numero_endpoint} EP
                      {prodottoBase.scalabile && ` (scalabile +€${prodottoBase.prezzo_scalabilita}/${prodottoBase.incremento_scalabilita} EP)`}
                    </p>
                    {prodottoBase.descrizione_breve && (
                      <p className="text-xs text-gray-400 mt-1">{prodottoBase.descrizione_breve}</p>
                    )}
                  </div>
                </div>
                {isBaseSelected && (
                  <div className="mt-3 ml-7">
                    <label className="block text-xs text-gray-500 mb-1">Numero Endpoint *</label>
                    <input
                      type="number"
                      value={data.numero_endpoint || ''}
                      onChange={handleEndpointChange}
                      min={10}
                      step={10}
                      className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                    {prodottoBase.numero_endpoint > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Minimo inclusi: {prodottoBase.numero_endpoint}</p>
                    )}
                    {fieldError('numero_endpoint')}
                  </div>
                )}
              </div>

              {/* Add-on: NS */}
              {prodottoNS && (
                <div className={`p-4 rounded-lg border border-gray-200 bg-white ${(!isBaseSelected || mode === 'bundle') ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!data.offerta_sorveglianza_rete && mode !== 'bundle'}
                      disabled={!isBaseSelected || mode === 'bundle'}
                      onChange={(e) => handleNSToggle(e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Wifi size={16} className="text-blue-500" />
                        <span className="text-sm font-medium text-gray-800">Network Surveillance</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">€{prodottoNS.prezzo_base} flat</p>
                      {prodottoNS.descrizione_breve && <p className="text-xs text-gray-400 mt-1">{prodottoNS.descrizione_breve}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Add-on: Awareness */}
              {prodottoAwareness && (
                <div className={`p-4 rounded-lg border border-gray-200 bg-white ${(!isBaseSelected || mode === 'bundle') ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={data.awareness_enabled && mode !== 'bundle'}
                      disabled={!isBaseSelected || mode === 'bundle'}
                      onChange={(e) => handleAwarenessToggle(e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Eye size={16} className="text-green-500" />
                        <span className="text-sm font-medium text-gray-800">Awareness Training</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        €{prodottoAwareness.prezzo_base}/{prodottoAwareness.numero_awareness_seats || 10} utenze
                        {prodottoAwareness.scalabile && ` (scalabile +€${prodottoAwareness.prezzo_scalabilita}/${prodottoAwareness.incremento_scalabilita} utenze)`}
                      </p>
                    </div>
                  </div>
                  {data.awareness_enabled && mode !== 'bundle' && (
                    <div className="mt-3 ml-7">
                      <label className="block text-xs text-gray-500 mb-1">Numero Utenze</label>
                      <input
                        type="number"
                        value={data.awareness_seats || ''}
                        onChange={(e) => onChange({ ...data, awareness_seats: parseInt(e.target.value, 10) || 0 })}
                        min={1}
                        className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                      <span className="text-xs text-gray-500 ml-2">utenze</span>
                    </div>
                  )}
                </div>
              )}

              {/* Add-on: MSSP */}
              {prodottoMSSP && (
                <div className={`p-4 rounded-lg border border-gray-200 bg-white ${(!isBaseSelected || mode === 'bundle') ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={(data.servizio_mssp_monitoring || false) && mode !== 'bundle'}
                      disabled={!isBaseSelected || mode === 'bundle'}
                      onChange={(e) => handleMSSPToggle(e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Monitor size={16} className="text-purple-500" />
                        <span className="text-sm font-medium text-gray-800">Monitoraggio Cyber</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        €{prodottoMSSP.prezzo_base} / {prodottoMSSP.numero_gettoni} gettoni × {prodottoMSSP.ore_per_gettone}h
                      </p>
                    </div>
                  </div>
                  {data.servizio_mssp_monitoring && mode !== 'bundle' && (
                    <div className="mt-3 ml-7 flex items-center gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Gettoni</label>
                        <input
                          type="number"
                          value={data.gettoni_totali || ''}
                          onChange={(e) => onChange({ ...data, gettoni_totali: parseInt(e.target.value, 10) || 0 })}
                          min={1}
                          className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          placeholder="20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Ore/gettone</label>
                        <input
                          type="number"
                          value={prodottoMSSP.ore_per_gettone || 2}
                          readOnly
                          className="w-20 px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-not-allowed"
                        />
                      </div>
                      {data.gettoni_totali > 0 && (
                        <div className="text-xs text-gray-500 mt-4">
                          = {data.gettoni_totali * (prodottoMSSP.ore_per_gettone || 2)}h totali
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
          )}
          </div>

          {/* ━━ RIGHT COLUMN ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {bundles.length > 0 && (
            <div>
            {isBaseSelected && selectedProdotto ? (
              /* Base selezionato → a destra mostra il calcolatore prezzo */
              <div className="lg:sticky lg:top-0">
                <PricingCalculator
                  prodotto={selectedProdotto}
                  numEndpoint={data.numero_endpoint || 0}
                  awarenessSeats={data.awareness_seats || 0}
                  awarenessEnabled={data.awareness_enabled}
                  prodottoAwareness={prodottoAwareness}
                  nsEnabled={!!data.offerta_sorveglianza_rete}
                  prodottoNS={prodottoNS}
                  msspEnabled={data.servizio_mssp_monitoring || false}
                  prodottoMSSP={prodottoMSSP}
                  gettoniTotali={data.gettoni_totali || 0}
                  isBundle={false}
                />
              </div>
            ) : (
              /* Default / Bundle selezionato → mostra i bundle */
              <>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Scegli un Bundle</h3>

              <div className="space-y-3">
                {bundles.map((bundle) => {
                  const isSelected = data._bundleId === bundle.id;
                  const includes = [];
                  if (bundle.numero_endpoint > 0) includes.push(`${bundle.numero_endpoint} EP`);
                  if (bundle.awareness_inclusa) includes.push(`Awareness (${bundle.numero_awareness_seats} utenze)`);
                  if ((bundle.descrizione_breve || '').toLowerCase().includes('network')) includes.push('Network Surveillance');
                  if (bundle.numero_gettoni > 0) includes.push(`MSSP (${bundle.numero_gettoni} gettoni)`);

                  return (
                    <div
                      key={bundle.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-300'
                          : 'border-gray-200 bg-white hover:border-yellow-200'
                      } ${mode === 'componi' && isBaseSelected ? 'opacity-40' : ''}`}
                      onClick={() => {
                        if (mode === 'componi' && isBaseSelected) return;
                        handleBundleSelect(bundle);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="bundle"
                          checked={isSelected}
                          readOnly
                          disabled={mode === 'componi' && isBaseSelected}
                          className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 focus:ring-yellow-500 accent-yellow-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Package size={16} className="text-yellow-600" />
                            <span className="text-sm font-medium text-gray-800">{bundle.nome_prodotto}</span>
                            <span className="text-xs font-bold bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">BUNDLE</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            €{bundle.prezzo_base}/anno
                            {bundle.scalabile && ` (scalabile +€${bundle.prezzo_scalabilita}/${bundle.incremento_scalabilita} EP)`}
                          </p>
                          {bundle.descrizione_breve && (
                            <p className="text-xs text-gray-400 mt-1">{bundle.descrizione_breve}</p>
                          )}
                          {includes.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {includes.map((inc, i) => (
                                <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                  ✓ {inc}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="mt-3 ml-7 space-y-3" onClick={(e) => e.stopPropagation()}>
                          {/* Endpoint */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Numero Endpoint *</label>
                            <input
                              type="number"
                              value={data.numero_endpoint || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleEndpointChange(e);
                              }}
                              min={10}
                              step={10}
                              className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                            />
                            {bundle.numero_endpoint > 0 && (
                              <p className="text-xs text-gray-500 mt-1">Minimo inclusi: {bundle.numero_endpoint}</p>
                            )}
                          </div>

                          {/* Awareness Seats (se inclusa nel bundle) */}
                          {data.awareness_enabled && (
                            <div>
                              <label className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                                <Eye size={12} className="text-green-500" />
                                Awareness — Numero Utenze
                              </label>
                              <input
                                type="number"
                                value={data.awareness_seats || ''}
                                onChange={(e) => onChange({ ...data, awareness_seats: parseInt(e.target.value, 10) || 0 })}
                                min={1}
                                className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                              />
                              <span className="text-xs text-gray-500 ml-2">utenze</span>
                              {bundle.numero_awareness_seats > 0 && (
                                <p className="text-xs text-gray-500 mt-1">Incluse nel bundle: {bundle.numero_awareness_seats}</p>
                              )}
                            </div>
                          )}

                          {/* Gettoni (se inclusi nel bundle) */}
                          {data.servizio_mssp_monitoring && (
                            <div className="flex items-end gap-3">
                              <div>
                                <label className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                                  <Monitor size={12} className="text-purple-500" />
                                  Monitoraggio Cyber — Gettoni
                                </label>
                                <input
                                  type="number"
                                  value={data.gettoni_totali || ''}
                                  onChange={(e) => onChange({ ...data, gettoni_totali: parseInt(e.target.value, 10) || 0 })}
                                  min={1}
                                  className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Ore/gettone</label>
                                <input
                                  type="number"
                                  value={data.ore_per_gettone || bundle.ore_per_gettone || 2}
                                  readOnly
                                  className="w-20 px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-not-allowed"
                                />
                              </div>
                              {data.gettoni_totali > 0 && (
                                <div className="text-xs text-gray-500 mb-2">
                                  = {data.gettoni_totali * (data.ore_per_gettone || bundle.ore_per_gettone || 2)}h totali
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
            )}
            </div>
          )}

          </div>{/* end grid */}

          {fieldError('listino_prodotto_base')}
        </div>
      )}

      {/* Scroll arrow */}
      {showArrow && (
        <div className="flex justify-center mt-2">
          <button
            type="button"
            onClick={scrollDown}
            title="Scorri per vedere tutti i prodotti"
            className="group flex flex-col items-center animate-bounce text-yellow-600 hover:text-yellow-700 transition-colors focus:outline-none"
          >
            <ChevronDown className="w-7 h-7" />
            <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500 mt-0.5">
              Scorri per vedere altro
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
